<div
  *ngIf="show"
  class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 backdrop-blur-xs p-4 overflow-y-auto"
>
  <div class="bg-white rounded-2xl w-full max-w-xl shadow-2xl border border-gray-100 overflow-hidden my-8 animate-fade-in-up">
    
    <!-- Modal Header -->
    <div class="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
      <div class="flex items-center space-x-2.5">
        <div class="p-2 rounded-lg bg-indigo-50 text-indigo-600">
          <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <div>
          <h2 class="text-lg font-bold text-gray-900">Security & Two-Factor Authentication</h2>
          <p class="text-xs text-gray-500">Manage TOTP authenticator and backup recovery codes</p>
        </div>
      </div>

      <button
        type="button"
        (click)="onClose()"
        class="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition cursor-pointer"
      >
        <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <!-- Feedback Alerts -->
    <div class="px-6 pt-4" *ngIf="errorMessage || successMessage">
      <div *ngIf="errorMessage" class="bg-red-50 border-l-4 border-red-500 p-3 rounded-r-md text-xs text-red-700 font-medium">
        {{ errorMessage }}
      </div>
      <div *ngIf="successMessage" class="bg-green-50 border-l-4 border-green-500 p-3 rounded-r-md text-xs text-green-700 font-medium">
        {{ successMessage }}
      </div>
    </div>

    <!-- Modal Body -->
    <div class="p-6">
      
      <!-- ================= TAB 1: OVERVIEW ================= -->
      <div *ngIf="activeTab === 'overview'" class="space-y-6">
        
        <!-- Status Card -->
        <div class="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200/60 rounded-xl p-4 flex items-center justify-between">
          <div class="flex items-center space-x-3">
            <div class="h-10 w-10 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div>
              <div class="flex items-center space-x-2">
                <span class="text-sm font-bold text-gray-900">2FA Protection</span>
                <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                  Enforced
                </span>
              </div>
              <p class="text-xs text-gray-600 mt-0.5">Authenticator app (TOTP) is active for this account.</p>
            </div>
          </div>
        </div>

        <!-- Management Cards -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          
          <!-- Recovery Codes Option -->
          <div class="border border-gray-200 rounded-xl p-4 hover:border-indigo-300 hover:shadow-xs transition-all duration-150 flex flex-col justify-between">
            <div>
              <div class="flex items-center space-x-2 text-indigo-600 mb-2">
                <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
                <h3 class="text-sm font-bold text-gray-900">Recovery Codes</h3>
              </div>
              <p class="text-xs text-gray-500 leading-relaxed">
                View your emergency backup codes or generate a fresh set if you suspect they have leaked.
              </p>
            </div>
            <button
              type="button"
              (click)="setTab('recovery_codes')"
              class="mt-4 w-full text-center px-3 py-2 bg-gray-50 hover:bg-indigo-50 hover:text-indigo-700 text-xs font-semibold text-gray-700 rounded-lg border border-gray-200 transition cursor-pointer"
            >
              Manage Recovery Codes &rarr;
            </button>
          </div>

          <!-- Reconfigure Authenticator Option -->
          <div class="border border-gray-200 rounded-xl p-4 hover:border-indigo-300 hover:shadow-xs transition-all duration-150 flex flex-col justify-between">
            <div>
              <div class="flex items-center space-x-2 text-indigo-600 mb-2">
                <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                <h3 class="text-sm font-bold text-gray-900">Reconfigure App</h3>
              </div>
              <p class="text-xs text-gray-500 leading-relaxed">
                Pair a new phone or switch your authenticator application (Google Authenticator, 1Password, etc.).
              </p>
            </div>
            <button
              type="button"
              (click)="setTab('reconfigure')"
              class="mt-4 w-full text-center px-3 py-2 bg-gray-50 hover:bg-indigo-50 hover:text-indigo-700 text-xs font-semibold text-gray-700 rounded-lg border border-gray-200 transition cursor-pointer"
            >
              Re-pair Authenticator &rarr;
            </button>
          </div>

        </div>

      </div>

      <!-- ================= TAB 2: RECOVERY CODES ================= -->
      <div *ngIf="activeTab === 'recovery_codes'" class="space-y-4">
        
        <!-- Mandatory Warning Alert -->
        <div class="bg-amber-50 border-l-4 border-amber-400 p-3.5 rounded-r-md">
          <div class="flex">
            <div class="shrink-0">
              <svg class="h-5 w-5 text-amber-500" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <h4 class="text-xs font-bold text-amber-900 uppercase tracking-wide">Critical Security Notice</h4>
              <p class="mt-1 text-xs text-amber-800 leading-relaxed">
                Recovery codes grant full access to your account if your authenticator app is lost.
                <strong>Never share or email them</strong>. Store them safely in a password manager.
              </p>
            </div>
          </div>
        </div>

        <!-- Codes Display -->
        <div *ngIf="recoveryCodes.length > 0" class="bg-gray-50 p-4 rounded-xl border border-gray-200">
          <div class="grid grid-cols-2 gap-2 font-mono text-xs text-gray-800">
            <div *ngFor="let code of recoveryCodes; let i = index" class="bg-white p-2 rounded border border-gray-200 text-center font-medium shadow-xs">
              <span class="text-gray-400 mr-1">{{ i + 1 }}.</span> {{ code }}
            </div>
          </div>

          <!-- Actions -->
          <div class="mt-3 flex items-center justify-between">
            <button
              type="button"
              (click)="rotateRecoveryCodes()"
              [disabled]="loading"
              class="text-xs text-red-600 hover:text-red-800 font-medium transition cursor-pointer disabled:opacity-50"
            >
              🔄 Generate New Set
            </button>

            <div class="flex items-center space-x-2">
              <button
                type="button"
                (click)="copyRecoveryCodes(recoveryCodes)"
                class="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition cursor-pointer"
              >
                {{ copiedRecoveryCodes ? '✓ Copied' : 'Copy All' }}
              </button>
              <button
                type="button"
                (click)="downloadRecoveryCodes(recoveryCodes)"
                class="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition cursor-pointer"
              >
                Download (.txt)
              </button>
            </div>
          </div>
        </div>

        <div *ngIf="loading && recoveryCodes.length === 0" class="py-8 text-center text-xs text-gray-500">
          Loading recovery codes...
        </div>

        <div class="pt-2 flex justify-start">
          <button
            type="button"
            (click)="setTab('overview')"
            class="text-xs font-medium text-indigo-600 hover:text-indigo-800 transition cursor-pointer"
          >
            &larr; Back to Security Overview
          </button>
        </div>
      </div>

      <!-- ================= TAB 3: RECONFIGURE AUTHENTICATOR ================= -->
      <div *ngIf="activeTab === 'reconfigure'" class="space-y-4">
        
        <div class="bg-blue-50 border-l-4 border-blue-400 p-3 rounded-r-md text-xs text-blue-800">
          Scanning this QR code will replace your existing 2FA configuration. Make sure to complete all steps.
        </div>

        <div *ngIf="qrCodeDataUrl" class="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-xl border border-gray-200">
          <div class="bg-white p-2 rounded-lg shadow-sm border border-gray-100">
            <img [src]="qrCodeDataUrl" alt="TOTP QR Code" class="w-40 h-40 object-contain" />
          </div>

          <div class="mt-3 w-full text-center">
            <div class="flex items-center justify-center space-x-2 bg-white px-2 py-1 rounded border border-gray-300 font-mono text-xs text-gray-800 select-all">
              <span class="truncate max-w-[200px]">{{ secret }}</span>
              <button
                type="button"
                (click)="copySecret()"
                class="text-indigo-600 hover:text-indigo-800 font-sans text-xs font-semibold transition cursor-pointer"
              >
                {{ copiedSecret ? '✓ Copied' : 'Copy' }}
              </button>
            </div>
          </div>
        </div>

        <div>
          <label for="reconfigure-totp" class="block text-xs font-semibold text-gray-700 mb-1">
            Enter 6-digit code from your new app to confirm:
          </label>
          <input
            [(ngModel)]="totpCode"
            id="reconfigure-totp"
            name="reconfigureTotp"
            type="text"
            inputmode="numeric"
            pattern="[0-9]*"
            maxlength="6"
            placeholder="000000"
            class="block w-full text-center tracking-[0.4em] font-mono text-xl py-2 rounded-lg border border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>

        <div class="flex items-center justify-between pt-2">
          <button
            type="button"
            (click)="setTab('overview')"
            class="text-xs text-gray-500 hover:text-gray-700 transition cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            (click)="confirmReconfigure()"
            [disabled]="loading || totpCode.trim().length !== 6"
            class="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-xs font-semibold shadow-sm disabled:opacity-50 transition cursor-pointer"
          >
            {{ loading ? 'Updating...' : 'Confirm & Save' }}
          </button>
        </div>

      </div>

    </div>

    <!-- Modal Footer -->
    <div class="px-6 py-3 bg-gray-50/50 border-t border-gray-100 flex justify-end">
      <button
        type="button"
        (click)="onClose()"
        class="px-4 py-2 bg-white hover:bg-gray-100 text-gray-700 border border-gray-200 rounded-lg text-xs font-semibold transition cursor-pointer shadow-xs"
      >
        Close
      </button>
    </div>

  </div>
</div>
